import React, { FC, ReactElement, ReactNode } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Link, Stack, SxProps, styled } from '@mui/material';

import { useIsMobile } from '~components/hooks/utils/useIsMobile';
import { PointerCatcher } from '~components/hooks/utils/usePointerCatcher';
import AppColors from '~components/theme/styles/colors';

const DropdownContainer = styled(Box, { shouldForwardProp: prop => prop !== 'menuSx' && prop !== 'show' })<{
  show?: number;
  menuSx?: any;
}>(({ show, menuSx }) => ({
  padding: '0.25rem',
  backgroundColor: AppColors.primary[3],
  borderRadius: '10px',
  zIndex: 12,
  top: '10px',
  position: 'absolute',
  opacity: show ? 1 : 0,
  pointerEvents: show ? undefined : 'none',
  transition: 'opacity 0.1s ease',
  ...menuSx,
}));

export const DropdownLink = styled(Link)({
  textDecoration: 'none',
  fontWeight: '600',
  color: AppColors.neutralGrey[3],
});

export const DropdownOptionButton = styled(Button)({
  width: '95%',
  color: AppColors.neutralGrey[3],
  textAlign: 'left',
  margin: '5px',
  fontWeight: '400',

  '&:hover': {
    backgroundColor: AppColors.primary[2],
  },
});

const DropdownButton = styled(Button, { shouldForwardProp: prop => prop !== 'buttonSx' })<{ buttonSx?: any }>(
  ({ buttonSx }) => ({
    border: 0,
    height: '40px',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: AppColors.primary[2],
    },
    ...buttonSx,
    backgroundColor: buttonSx && buttonSx.backgroundColor ? buttonSx.backgroundColor : AppColors.primary[3],
  }),
);

const Chevron = styled(ExpandMoreIcon)<{ expanded: number }>(({ expanded }) => ({
  width: 18,
  height: 18,
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.1s linear',
}));

const DropdownContent: FC<{ menuSx?: SxProps; show: number; children: ReactNode }> = ({
  menuSx,
  show,
  children,
}) => {
  return (
    <Box sx={{ position: 'relative' }}>
      <DropdownContainer menuSx={menuSx} show={show}>
        <Stack direction={'column'}>{children}</Stack>
      </DropdownContainer>
    </Box>
  );
};

export const Dropdown: FC<{
  buttonTitle: string | ReactElement;
  customControl?: { show: boolean; component: ReactElement; onClickOutside: () => void };
  menuSx?: SxProps;
  hideChevron?: boolean;
  buttonSx?: SxProps;
  children: ReactNode;
  preventOnClickInject?: boolean;
}> = ({ hideChevron, children, buttonTitle, customControl, menuSx, buttonSx, preventOnClickInject }) => {
  const [isContainerOpen, setContainerOpen] = React.useState<boolean>(false);
  const isMobile = useIsMobile();
  const MemoContent = React.memo(DropdownContent);
  return (
    <PointerCatcher
      onOutside={() => {
        customControl?.onClickOutside() ?? null;
        setContainerOpen(false);
      }}
    >
      {customControl?.component ?? (
        <DropdownButton
          disableElevation
          disableRipple
          disableFocusRipple
          variant="primary"
          disableTouchRipple
          onClick={() => setContainerOpen(!isContainerOpen)}
          buttonSx={buttonSx}
          sx={{ minWidth: isMobile ? '30px' : undefined, ...buttonSx }}
        >
          <Stack direction={'row'} alignItems={'center'} mr={-0.75}>
            {buttonTitle}
            {!hideChevron ? <Chevron sx={{ ml: 0.5 }} expanded={isContainerOpen ? 1 : 0} /> : null}
          </Stack>
        </DropdownButton>
      )}
      <MemoContent show={customControl?.show || isContainerOpen ? 1 : 0} menuSx={menuSx}>
        {React.Children.map(children, child =>
          child && React.isValidElement(child) && !preventOnClickInject
            ? React.cloneElement(
                child,
                'onClick' in child.props
                  ? { ...child.props, onClick: () => setContainerOpen(false) }
                  : {
                      ...child.props,
                      onClick: () => {
                        child.props.onClick ? child.props.onClick() : undefined;
                        setContainerOpen(false);
                      },
                    },
              )
            : child ?? null,
        )}
      </MemoContent>
    </PointerCatcher>
  );
};
